import {useCallback, useEffect, useState} from "react";
import {Images} from "lucide-react";
import {createAdminCmsCarouselSlide, deleteAdminCmsCarouselSlide, listAdminCmsCarousel, updateAdminCmsCarouselSlide, uploadAdminMedia, type AdminCmsCarouselSlide} from "@corporatesclub/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs, AdminRowTrailing} from "./ui";

const emptyForm = {nume: "", url: "", ordine: 0, imagine: ""};

export function CmsCarouselView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("continut");
  const [items, setItems] = useState<AdminCmsCarouselSlide[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminCmsCarousel({page, pageSize: 20}).then((r) => { setItems(r.items); setTotalPages(r.totalPages); }).catch((e: Error) => setErr(e.message));
  }, [page]);
  useEffect(() => { load(); }, [load]);
  async function onUpload(id: number, file: File) {
    const r = await uploadAdminMedia({folder: "images/casute/", table: "pagini_carusel", column: "imagine", id, file});
    setForm((f) => ({...f, imagine: r.filename}));
    load();
  }
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(s: AdminCmsCarouselSlide) {
    setOpenId(s.id);
    setForm({nume: s.nume, url: s.url, ordine: s.ordine, imagine: s.imagine});
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          await createAdminCmsCarouselSlide(form);
          adminToast.success("Slide added.");
        } else if(typeof openId === "number") {
          await updateAdminCmsCarouselSlide(openId, form);
          adminToast.success("Slide saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete?", async () => {
      await deleteAdminCmsCarouselSlide(id);
      closeModal(false);
      load();
      adminToast.success("Slide deleted.");
    });
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.continut.title}
      description="Homepage carousel slides"
      breadcrumbs={[
        {label: ADMIN_SECTION_NAV.continut.title, href: "/vadmin/continut"},
        {label: "Carousel"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add slide" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="continut" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(s) => s.id}
        getTitle={(s) => s.nume}
        getSubtitle={(s) => s.url}
        getMeta={(s) => <AdminBadge variant="secondary">Ord. {s.ordine}</AdminBadge>}
        getSearchText={(s) => adminSearchText(s.id, s.nume, s.url, s.ordine, s.imagine)}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No slides"
        emptyDescription="Add a new carousel slide."
        searchPlaceholder="Search slide, URL, ordine..."
        getRowActions={(s) => (
          <AdminRowTrailing onDelete={() => removeItem(s.id)} deleteLabel="Delete slide" />
        )}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New slide" : "Edit slide"}
        icon={Images}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          <AdminField label="Name">
            <Input value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} />
          </AdminField>
          <AdminField label="URL">
            <Input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} />
          </AdminField>
          <AdminField label="Order">
            <Input type="number" value={form.ordine} onChange={(e) => setForm({...form, ordine: Number(e.target.value)})} />
          </AdminField>
          {form.imagine ? (
            <div className="admin-detail-panel__section">
              <p><img src={`https://www.corporatesclub.com/images/casute/${form.imagine}`} alt="" /> {form.imagine}</p>
            </div>
          ) : null}
          {typeof openId === "number" ? (
            <AdminField label="Imagine">
              <Input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if(f) onUpload(openId, f); }} />
            </AdminField>
          ) : null}
        </div>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
